import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQueryWithReauth } from 'src/apis/baseQuery';
import type { RootState } from 'src/store';
import {
  DataDTOClientSsoProviderCrud,
  DataDTOClientSsoProviderView,
  DataDTOClientView,
  DataDTOPagedDataDataDTOClientSsoProviderView,
} from 'src/types/api/generated/api';
import { PaginationParams } from 'src/types/common';

const patchCachedIsEnabled = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  getState: () => unknown,
  providerId: number,
  isEnabled: boolean,
) =>
  ssoProviderAPI.util
    .selectCachedArgsForQuery(getState() as RootState, 'getSsoProviders')
    .map((args) =>
      dispatch(
        ssoProviderAPI.util.updateQueryData('getSsoProviders', args, (draft) => {
          const row = draft.rows.find((r: DataDTOClientSsoProviderView) => r.id === providerId);
          if (row) {
            row.isEnabled = isEnabled;
          }
        }),
      ),
    );

export const ssoProviderAPI = createApi({
  reducerPath: 'ssoProviderAPI',
  baseQuery: axiosBaseQueryWithReauth({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ['SsoProvider'],
  endpoints: (builder) => ({
    getSsoProviders: builder.query<
      DataDTOPagedDataDataDTOClientSsoProviderView,
      PaginationParams & { clientId: number }
    >({
      query: ({ clientId, page, pageSize, orderBy, filters, searchTerm }) => ({
        url: `Clients/${clientId}/SsoProviders`,
        method: 'GET',
        params: {
          page,
          pageSize,
          orderBy,
          searchTerm,
          filters: filters ? JSON.stringify(filters) : undefined,
        },
      }),
      providesTags: [{ type: 'SsoProvider', id: 'LIST' }],
    }),

    getClientForSsoProviders: builder.query<DataDTOClientView, { clientId: number }>({
      query: ({ clientId }) => ({
        url: `Clients/${clientId}`,
        method: 'GET',
        params: { clientId },
      }),
    }),

    createSsoProvider: builder.mutation<
      DataDTOClientSsoProviderView,
      { clientId: number; body: DataDTOClientSsoProviderCrud }
    >({
      query: ({ clientId, body }) => ({
        url: `Clients/${clientId}/SsoProviders`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'SsoProvider', id: 'LIST' }],
    }),

    updateSsoProvider: builder.mutation<
      DataDTOClientSsoProviderView,
      { clientId: number; providerId: number; body: DataDTOClientSsoProviderCrud }
    >({
      query: ({ clientId, providerId, body }) => ({
        url: `Clients/${clientId}/SsoProviders/${providerId}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted({ providerId, body }, { dispatch, queryFulfilled, getState }) {
        const patches = patchCachedIsEnabled(dispatch, getState, providerId, body.isEnabled);

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
          dispatch(ssoProviderAPI.util.invalidateTags([{ type: 'SsoProvider', id: 'LIST' }]));
        }
      },
      invalidatesTags: [{ type: 'SsoProvider', id: 'LIST' }],
    }),
    disableSsoProvider: builder.mutation<boolean, { clientId: number; providerId: number }>({
      query: ({ clientId, providerId }) => ({
        url: `Clients/${clientId}/SsoProviders/${providerId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ providerId }, { dispatch, queryFulfilled, getState }) {
        const patches = patchCachedIsEnabled(dispatch, getState, providerId, false);

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
          dispatch(ssoProviderAPI.util.invalidateTags([{ type: 'SsoProvider', id: 'LIST' }]));
        }
      },
      invalidatesTags: [{ type: 'SsoProvider', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetSsoProvidersQuery,
  useLazyGetSsoProvidersQuery,
  useGetClientForSsoProvidersQuery,
  useCreateSsoProviderMutation,
  useUpdateSsoProviderMutation,
  useDisableSsoProviderMutation,
} = ssoProviderAPI;
